/**
 * ECharts Theme Configuration
 *
 * CSS custom properties are resolved at runtime for dark mode compatibility.
 */

import { useEffect, useState } from "react";

/**
 * Bumps whenever the <html> theme class changes (ThemeToggle, OS switch).
 * Charts add this to their option-memo deps so getChartTheme() re-reads the
 * CSS tokens instead of keeping mount-time colors.
 */
export function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setVersion((v) => v + 1));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return version;
}

export function getChartTheme() {
  // Read CSS custom properties at runtime
  const style =
    typeof document !== "undefined"
      ? getComputedStyle(document.documentElement)
      : null;

  const foreground =
    style?.getPropertyValue("--foreground").trim() || "oklch(0.95 0.01 250)";
  const mutedForeground =
    style?.getPropertyValue("--muted-foreground").trim() ||
    "oklch(0.65 0.02 250)";
  const border =
    style?.getPropertyValue("--border").trim() || "oklch(0.25 0.03 250)";
  const primary =
    style?.getPropertyValue("--primary").trim() || "oklch(0.75 0.18 195)";

  return {
    foreground,
    mutedForeground,
    border,
    primary,
    // Chart-specific colors
    success: "#22c55e",
    warning: "#eab308",
    danger: "#ef4444",
    info: "#3b82f6",
  };
}

export const CHART_COLORS = [
  "#06b6d4", // cyan
  "#f97316", // orange
  "#8b5cf6", // violet
  "#22c55e", // green
  "#ef4444", // red
  "#eab308", // yellow
  "#ec4899", // pink
  "#14b8a6", // teal
];
